import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { createMutationErrorHandler } from "@/_utils/errorHandling.utils";
import { TCustomQueryProps } from "@/common/type/hooks.types";

import { IUser } from "../interfaces/user";
import {
  createUser,
  deleteUser,
  detachUserTeam,
  getUserById,
  updateUser,
} from "../services";

export const useCreateUser = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<IUser>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user: IUser) => createUser(user),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });

      handleSuccess?.(variables);
    },
    onError: (error: AxiosError) => {
      // Use the custom error handler that handles validation errors
      const defaultErrorHandler = createMutationErrorHandler(
        "Erreur lors de la création de l'utilisateur"
      );

      if (handleError) {
        handleError(error);
      } else {
        defaultErrorHandler(error);
      }
    },
  });
};

export const useGetUserById = (id: string) => {
  return useQuery({
    queryKey: ["getUserById", id],
    queryFn: () => getUserById(id),
    enabled: !!id,
  });
};

export const useUpdateUser = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<IUser>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user: IUser) => updateUser(user),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getUserById", id] });

      handleSuccess?.(variables);
    },
    onError: (error: AxiosError) => {
      const defaultErrorHandler = createMutationErrorHandler(
        "Erreur lors de la mise à jour de l'utilisateur"
      );

      if (handleError) {
        handleError(error);
      } else {
        defaultErrorHandler(error);
      }
    },
  });
};

export const useDetachUserFromTeam = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<IUser>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, teamId }: { id: string; teamId: string }) =>
      detachUserTeam(id, teamId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });

      handleSuccess?.(data);
    },
    onError: (error: AxiosError) => {
      const defaultErrorHandler = createMutationErrorHandler(
        "Erreur lors du détachement de l'utilisateur"
      );

      if (handleError) {
        handleError(error);
      } else {
        defaultErrorHandler(error);
      }
    },
  });
};

export const useDeleteUser = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<IUser>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });

      handleSuccess?.();
    },
    onError: (error: AxiosError) => {
      const defaultErrorHandler = createMutationErrorHandler(
        "Erreur lors de la suppression de l'utilisateur"
      );

      if (handleError) {
        handleError(error);
      } else {
        defaultErrorHandler(error);
      }
    },
  });
};
